import { lazy, type ComponentType } from "react";

const RELOAD_FLAG = "chunk-reload-at";

/**
 * A dynamic import can fail because the deployed bundle was replaced while the
 * tab stayed open (the hashed chunk no longer exists) or because the network
 * blipped. Retry once, then reload the page once so the browser picks up the
 * fresh manifest. The session flag stops us from looping on a real outage.
 */
export async function importWithRetry<T>(factory: () => Promise<T>): Promise<T> {
  try {
    return await factory();
  } catch (err) {
    await new Promise((r) => setTimeout(r, 600));
    try {
      return await factory();
    } catch (err2) {
      const last = Number(sessionStorage.getItem(RELOAD_FLAG) || 0);
      if (Date.now() - last > 30_000) {
        sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
        window.location.reload();
        // Never resolves — the page is on its way out.
        await new Promise(() => {});
      }
      throw err2 instanceof Error
        ? new Error("We couldn't load this part of the site. Please refresh the page.")
        : err2;
    }
  }
}

/** Drop-in replacement for React.lazy that survives stale chunk hashes. */
export function lazyWithRetry<T extends ComponentType<unknown>>(factory: () => Promise<{ default: T }>) {
  return lazy(() => importWithRetry(factory));
}
